import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getActiveOrgEmail, request } from '@papan/api-client'
import {
  capAtLeast, capSetOf, isPlatformAdminOrg, platformCan,
  type CapValue, type PlatformArea, type PlatformCapabilities,
} from '@papan/types'

/**
 * The caller's RESOLVED capabilities for a tenant — the client's replacement
 * for `userRole === 'admin'` gates. Backed by GET /api/orgs/:id/my-capabilities
 * (every cap resolved server-side for the active principal).
 *
 * - Keyed by (tenantId, orgEmail) so switching the active email refetches —
 *   resolved values are per-identity.
 * - Personal drives (isPersonal) short-circuit to owner powers: `can()` → true. A missing tenant id is NOT a
 *   personal drive: `can()` → false (#1102 — it used to answer true, so an org view without its tenant id yet offered
 *   every action).
 * - Tenant caps unresolved (still loading / non-member) → `can()` is false
 *   (conservative: never offer an action the server would reject).
 *
 * Tenant-scoped caps only. Per-FOLDER gates use the row's `effectivePermission`
 * (the resolved manage_folder_permission), not this hook.
 *
 * ── `unavailable` is NOT the same as denied (#341) ──────────────────────
 *
 * `can()` stays false when the fetch FAILS, which is the right security answer
 * and a terrible UX answer on its own: with the global `retry: 1`, two failed
 * requests leave `loading` false and every capability false — a state
 * byte-identical to a legitimate "you have no permissions". An org OWNER then
 * silently loses Delete organization, sees member management as read-only, and
 * reads as a non-admin on the grants page, with no error anywhere. Any cause
 * will do: a blip, a 500, a rate limit, an expired session.
 *
 * So callers get a THIRD state. `can()` keeps failing closed — nothing here
 * grants anything — while `unavailable` says the answer is unknown rather than
 * no, and `retry` offers the way out. Gating UI on `can()` alone is still safe;
 * it is just silent, so any surface that hides admin controls should render the
 * notice when `unavailable` is set.
 *
 * The sibling `useBudgetBillingAccess` below makes exactly this argument for the
 * *loading* case, where treating in-flight as denied broke every cold load. This
 * is the same bug one state over.
 */
export function useMyCapabilities(
  tenantId: string | undefined,
  isPersonal: boolean | undefined,
  orgEmail: string | undefined,
) {
  const enabled = !!tenantId && !isPersonal
  // #1210: the key always carries the identity the read is made as — a caller that passes no email (the version
  // panels) used to share one entry across org emails, so B was shown A's management capabilities for five minutes.
  const keyEmail = orgEmail ?? getActiveOrgEmail() ?? null
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-capabilities', tenantId, keyEmail],
    queryFn: () => request<{ capabilities: Record<string, CapValue> }>(`/api/orgs/${tenantId}/my-capabilities`),
    enabled,
    staleTime: 5 * 60_000,
  })
  const caps = data?.capabilities

  const can = useCallback((capability: string, required: CapValue): boolean => {
    if (isPersonal) return true // personal drive → implicit owner
    // No tenant id yet (an org view still loading its tenant) is not a personal drive: offer nothing (#1102)
    if (!tenantId) return false
    const set = capSetOf(capability)
    const value = caps?.[capability]
    if (!set || value === undefined) return false
    return capAtLeast(value, required, set)
  }, [caps, isPersonal, tenantId])

  return {
    can,
    // An org view with no tenant id yet is still LOADING, never a settled denial (CLAUDE.md: refuse only once
    // resolved) — `can()` answers false meanwhile, but callers that show refusal copy wait on this.
    loading: !isPersonal && (!tenantId || isLoading),
    /** The capability answer is UNKNOWN (fetch failed) — not "denied". See above. */
    unavailable: enabled && isError,
    /** Re-run the capability fetch; pairs with `unavailable` for a retry affordance. */
    retry: refetch,
  }
}

/**
 * May this user open the Usage & Billing shelf?
 *
 * The shelf is PLATFORM-ONLY: the ceiling imposed on every tenant, what each
 * metered feature costs, and the plan/pack catalog. A tenant's own budget,
 * spending limits and subscription are org configuration and live in
 * Settings → Organization, alongside members and teams, gated by the same
 * capabilities as before.
 *
 * The answer therefore mirrors `requirePlatformAdmin` exactly — BOTH the
 * platform role AND acting in the platform org. `platformRole` alone answers
 * "may this person administer the platform?", never "are they doing so right
 * now", so a platform admin on their personal drive would get a shelf whose
 * every request 403s `WRONG_TENANT_SCOPE`.
 *
 * Kept here, in one place, because the same predicate gates three call sites
 * (desktop taskbar, mobile burger, mobile route) and three copies of an access
 * rule is how they drift. It is also the shelf's ONLY gate now — the view no
 * longer re-derives it.
 *
 * NOT access control: `requirePlatformAdmin` is. This only decides whether an
 * entry point is worth offering.
 *
 * `loading` is always false — the answer comes from the session and the active
 * tenant id, with no capability query behind it, so there is no window where
 * "still fetching" and "denied" look alike.
 */
export function useBudgetBillingAccess(opts: {
  tenantId?: string
  platformRole?: string | null
}): BudgetBillingAccess {
  const allowed = opts.platformRole === 'admin' && isPlatformAdminOrg(opts.tenantId)

  // Phase 0 (decision D2): WHICH areas, and read vs write, now depend on the
  // caller's role inside the platform org — which only the server knows. The
  // entry-point answer above deliberately stays synchronous, so `loading` is
  // still false for it and no deep link regresses; the finer grant arrives
  // separately.
  const { data, isLoading } = useQuery({
    queryKey: ['platform-capabilities', opts.tenantId],
    queryFn: () => request<{ capabilities: PlatformCapabilities }>('/api/admin/my-capabilities'),
    enabled: allowed,
    staleTime: 5 * 60_000,
  })

  return {
    allowed,
    loading: false,
    capabilities: data?.capabilities,
    capabilitiesLoading: allowed && isLoading,
  }
}

/**
 * May this caller see the nav item for `area`?
 *
 * **Optimistic while the grant is in flight, and that is safe here rather than
 * lazy.** Nav visibility asks for `read`, and every role that confers platform
 * admin at all holds `read` on every area — `viewer`/`guest`, the weakest, are
 * read-everywhere. So the answer during loading is the answer for every
 * possible caller, and rendering it early costs nothing while treating loading
 * as denial would re-open the cold-load bug documented on `BudgetBillingAccess`
 * (a refresh or a pasted support link would bounce out of the console before
 * the grant landed).
 *
 * WRITE gates must NOT use this shape — pass the resolved capabilities and let
 * an absent grant read as denial, because a write control shown and then
 * refused is worse than one that appears a moment late.
 */
export function canSeePlatformArea(
  access: BudgetBillingAccess,
  area: PlatformArea,
): boolean {
  if (!access.allowed) return false
  if (!access.capabilities) return access.capabilitiesLoading
  return platformCan(access.capabilities, area, 'read')
}

/** May this caller WRITE in `area`? Fails closed while the grant is in flight. */
export function canEditPlatformArea(
  access: BudgetBillingAccess,
  area: PlatformArea,
): boolean {
  return access.allowed && platformCan(access.capabilities, area, 'write')
}

/**
 * `loading` is carried separately because "still fetching" and "denied" are the
 * same `false` from `can()`, and one caller acts on the difference: the
 * `/billing` route redirects to Settings when the viewer has no page there.
 * Treating the in-flight state as denial made that fire on every COLD load — a
 * refresh, a bookmark, a pasted support link — so no `/billing*` URL was
 * reachable except by clicking the taskbar button, which defeats the whole
 * point of giving the platform pages linkable `platform/` paths.
 *
 * The field is now always `false`, because the platform answer needs no query.
 * It is KEPT rather than removed: callers still branch on it, and the shape
 * survives the next gate that does need a fetch. Deleting it would quietly
 * re-open the cold-load bug for whoever adds that gate.
 */
export interface BudgetBillingAccess {
  allowed: boolean
  loading: boolean
  /**
   * Per-area grant resolved by the server (phase 0, decision D2). `undefined`
   * while in flight, or when `allowed` is false and the query never ran — the
   * two are distinguished by `capabilitiesLoading`, not by this field.
   */
  capabilities?: PlatformCapabilities
  /** True only while `allowed` and the grant query is in flight. */
  capabilitiesLoading: boolean
}
